import { useEffect, useMemo, useRef, useState } from "react";
import { HTTP_STATUSES, OPTION_STATUS } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { IMaterialsResponse, IMergedBidArea } from "@miplatform/types";
import { filterInactiveAttributes } from "@miplatform/utils";
import { useCustomerMaterials } from "@mibuildweb/hooks";

const RECENT_SUGGESTIONS_LIMIT = 25;

interface UseReplaceItemsViewModelParams {
  open: boolean;
  builderId: string;
  selectedItem: IMaterialsResponse | null;
  selectedArea: IMergedBidArea | null;
  planAreas: IMergedBidArea[];
}

export function useReplaceItemsViewModel({
  open,
  builderId,
  selectedItem,
  selectedArea,
  planAreas,
}: UseReplaceItemsViewModelParams) {
  const [search, setSearch] = useState("");
  const [replacementItem, setReplacementItem] =
    useState<IMaterialsResponse | null>(null);
  const [materialOptions, setMaterialOptions] = useState<IMaterialsResponse[]>(
    []
  );
  const [categorySuggestions, setCategorySuggestions] = useState<
    IMaterialsResponse[]
  >([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([]);
  const [isResultsOpen, setIsResultsOpen] = useState(false);

  const suggestionsCategoryId = useRef<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);
  const { isLoading, getCustomerMaterials } = useCustomerMaterials();

  const targetMaterialId = selectedItem?.materialId;
  const categoryId = selectedItem?.category?.categoryId;

  const matchingAreas = useMemo(() => {
    if (!targetMaterialId) return [];
    return planAreas.filter(
      (area) =>
        area.optionStatus !== OPTION_STATUS.REMOVED &&
        area.materials?.some(
          (m) =>
            m.materialId === targetMaterialId &&
            m.optionStatus !== OPTION_STATUS.REMOVED
        )
    );
  }, [planAreas, targetMaterialId]);

  useEffect(() => {
    if (!open) return;
    // initialize state
    setSearch("");
    setReplacementItem(null);
    setMaterialOptions([]);
    setCategorySuggestions([]);
    suggestionsCategoryId.current = null;
    setSelectedAreaIds(selectedArea ? [selectedArea.id] : []);
  }, [open, selectedArea?.id, targetMaterialId]);

  useEffect(() => {
    if (!open || replacementItem) return;
    if (!debouncedSearch) {
      setMaterialOptions([]);
      return;
    }
    getReplacementMaterials();
  }, [debouncedSearch, open, replacementItem]);

  const mapMaterials = (materials: IMaterialsResponse[]) =>
    filterInactiveAttributes(materials)
      .map((material: IMaterialsResponse) => ({
        ...material,
        materialId: material.id,
      }))
      .filter(
        (material: IMaterialsResponse) =>
          material.materialId !== targetMaterialId
      );

  // default suggestions before searching
  const getCategorySuggestions = async () => {
    if (!categoryId || suggestionsCategoryId.current === categoryId) return;
    suggestionsCategoryId.current = categoryId;
    const response = await getCustomerMaterials({
      accountId: builderId,
      filters: {
        category: { categoryId: { type: "guid", value: categoryId } },
      },
      sort: "modifiedOn desc",
      top: RECENT_SUGGESTIONS_LIMIT,
    });
    if (response.status === HTTP_STATUSES.OK) {
      setCategorySuggestions(mapMaterials(response.data));
    } else {
      suggestionsCategoryId.current = null;
    }
  };

  const getReplacementMaterials = async () => {
    const filters = {
      or: [
        { category: { itemCode: { contains: debouncedSearch } } },
        { category: { itemDescription: { contains: debouncedSearch } } },
        { manufacturerPartNumber: { contains: debouncedSearch } },
        { category: { categoryName: { contains: debouncedSearch } } },
      ],
    };

    const response = await getCustomerMaterials({
      accountId: builderId,
      filters,
    });
    if (response.status === HTTP_STATUSES.OK) {
      setMaterialOptions(mapMaterials(response.data));
    }
  };

  const handleSearchFocus = () => {
    setIsResultsOpen(true);
    getCategorySuggestions();
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setIsResultsOpen(true);
    if (replacementItem) setReplacementItem(null);
  };

  const handleSelectReplacement = (material: IMaterialsResponse) => {
    setReplacementItem(material);
    setSearch("");
    setMaterialOptions([]);
    setIsResultsOpen(false);
  };

  const isAllSelected =
    matchingAreas.length > 0 &&
    matchingAreas.every((area) => selectedAreaIds.includes(area.id));

  const handleToggleArea = (areaId: string, checked: boolean) => {
    setSelectedAreaIds((prev) =>
      checked ? [...prev, areaId] : prev.filter((id) => id !== areaId)
    );
  };

  const handleToggleAllAreas = () => {
    setSelectedAreaIds(isAllSelected ? [] : matchingAreas.map((a) => a.id));
  };

  const results = debouncedSearch ? materialOptions : categorySuggestions;
  const showResults = isResultsOpen && !replacementItem && results.length > 0;

  return {
    search,
    isLoading,
    replacementItem,
    results,
    matchingAreas,
    selectedAreaIds,
    isAllSelected,
    showResults,
    setIsResultsOpen,
    handleSearchFocus,
    handleSearchChange,
    handleSelectReplacement,
    handleToggleArea,
    handleToggleAllAreas,
  };
}
